/* Bulk-outreach: genereer concepten voor alle geselecteerde leads. */
import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import i18n from "i18next";
import { X, Loader2, Send, Sparkles } from "lucide-react";
import { FieldX } from "./FieldX";

/* ------------------------------------------------------------------ */
/*  Bulk-outreach modal                                                */
/* ------------------------------------------------------------------ */
function BulkOutreachModal({ leadIds, onClose, onDone }) {
    const { workspace } = useAuth();
    const [members, setMembers] = useState([]);
    const [onBehalf, setOnBehalf] = useState("");
    const [submitForApproval, setSubmitForApproval] = useState(true);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        api.get("/team/members").then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setRunning(true);
        try {
            const r = await api.post("/outreach/bulk-generate", {
                lead_ids: leadIds,
                submit_for_approval: submitForApproval,
                on_behalf_of_user_id: onBehalf || null,
            });
            toast.success(r.data.summary || i18n.t("pipeline_sales.drafts_ready"));
            onDone?.();
        } catch (err) { toast.error(err?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setRunning(false); }
    };

    const withLI = members.filter((m) => m.linkedin_url);
    const approvalReq = !!workspace?.approval_required;

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit} data-testid="bulk-outreach-modal"
                className="w-full max-w-lg bg-[var(--jr-surface)] border border-[var(--jr-brand)]/40 rounded-2xl p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl tracking-tight inline-flex items-center gap-2">
                            <Send className="text-[var(--jr-brand)]" size={18} /> {i18n.t("pipeline_sales.outreach_for", { count: leadIds.length })}
                        </h3>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{i18n.t("pipeline_sales.bo_sub_pre")} <span className="text-[var(--jr-brand)]">{i18n.t("pipeline_sales.bo_best_channel")}</span> {i18n.t("pipeline_sales.bo_sub_post")}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="bo-close" className="text-[var(--jr-text-3)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="space-y-3">
                    <FieldX label={i18n.t("pipeline_sales.lbl_onbehalf")} testid="bo-onbehalf">
                        <select value={onBehalf} onChange={(e) => setOnBehalf(e.target.value)}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none">
                            <option value="">{i18n.t("pipeline_sales.opt_ws_default")}</option>
                            {withLI.length === 0 && <option disabled>{i18n.t("pipeline_sales.opt_no_members")}</option>}
                            {withLI.map((m) => (
                                <option key={m.id} value={m.id}>{m.name} · {m.linkedin_handle || m.linkedin_url.replace(/^https?:\/\//, "")}</option>
                            ))}
                        </select>
                    </FieldX>

                    <label className="flex items-center gap-2 text-xs text-white cursor-pointer" data-testid="bo-submit-checkbox">
                        <input type="checkbox" checked={submitForApproval} onChange={(e) => setSubmitForApproval(e.target.checked)} />
                        <span>{i18n.t("pipeline_sales.bo_direct")} {approvalReq ? <span className="text-[var(--jr-amber)]">{i18n.t("pipeline_sales.bo_approval_req")}</span> : <span className="text-[var(--jr-text-3)]">{i18n.t("pipeline_sales.bo_no_approval")}</span>}</span>
                    </label>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={running} data-testid="bo-cancel"
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={running} data-testid="bo-run"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {running ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
                        {i18n.t("pipeline_sales.generate_drafts", { count: leadIds.length })}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default BulkOutreachModal;
